import { TransformedXAxisType } from '@api';
import { formatDecimalPlaces } from '@design/components/table/utils';
import { useWindowResize } from '@helpers';
import { getInstanceByDom, init } from 'echarts';
import React, { useEffect, useMemo, useRef } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { generatePieces, generateRangeArray } from './utils';

const DEFAULT_HEATMAP_PIECES_COUNT = 5;

export const heatmapTypes = ['apache-heatmap', 'apache-heatmap-calendar'];
export type HeatmapType = (typeof heatmapTypes)[number];

type BasicHeatmapSeries = {
  name: string;
  data: number[][];
};

type CalendarHeatmapSeries = {
  name: string;
  data: number[];
};

export type SeriesMap = {
  'apache-heatmap': BasicHeatmapSeries;
  'apache-heatmap-calendar': CalendarHeatmapSeries;
};

const isBasicHeatmapSeries = (
  series: BasicHeatmapSeries['data'] | CalendarHeatmapSeries['data']
): series is BasicHeatmapSeries['data'] => {
  return Array.isArray(series[0]) && series[0].length === 3;
};

export interface HeatmapChartProps<T extends keyof SeriesMap>
  extends ApacheChartBase {
  /**
   * Variant: `'apache-heatmap'` for a plain x/y matrix or
   * `'apache-heatmap-calendar'` for a date-aligned calendar heatmap.
   */
  chartType: keyof SeriesMap;
  /** Categories along the x-axis. For the calendar variant, also serves as the date stream. */
  xAxis: TransformedXAxisType;
  /** Categories along the y-axis. Required for `'apache-heatmap'`. */
  yAxis?: (string | number)[];
  /** Custom bucket boundaries for the calendar variant's piecewise legend. */
  ranges?: number[];
  /**
   * Heatmap data. For `'apache-heatmap'` this is `{name, data: [x, y, value][]}`;
   * for `'apache-heatmap-calendar'` it's `{name, data: number[]}`.
   */
  series: SeriesMap[T];
}

/**
 * Apache ECharts heatmap with two layouts:
 *
 * - `'apache-heatmap'` — classic 2-D matrix; cells coloured by value
 *   against a continuous scale shown as a horizontal `visualMap`.
 * - `'apache-heatmap-calendar'` — calendar layout showing one cell per
 *   day for a year, coloured by `series.data` value with a piecewise
 *   legend.
 */
export const HeatmapChart = <T extends keyof SeriesMap>({
  theme = 'dark_default',
  chartType = 'apache-heatmap',
  xAxis,
  yAxis,
  ranges,
  series,
  onInit,
  isModal,
}: HeatmapChartProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  const formattedSeries = useMemo(() => {
    const seriesData = series.data;

    if (isBasicHeatmapSeries(seriesData)) {
      return seriesData.map(i => {
        const value = i[2];

        if (typeof value === 'number') {
          return [i[0], i[1], formatDecimalPlaces(value)];
        }

        return i;
      });
    } else {
      return seriesData.reduce<[string | number, string | number][]>(
        (result, current, index) => {
          result.push([xAxis[index], formatDecimalPlaces(current)]);
          return result;
        },
        []
      );
    }
  }, [series, xAxis]);
  const valueArr = useMemo(() => {
    const seriesData = series.data;

    if (isBasicHeatmapSeries(seriesData)) {
      return seriesData
        .map(i => {
          return i[2];
        })
        .filter(i => typeof i === 'number');
    } else {
      return seriesData;
    }
  }, [series]);

  const heatMapOptions: Record<HeatmapType, {}> = useMemo(() => {
    const min = Math.min(...valueArr);
    const max = Math.max(...valueArr);

    return {
      'apache-heatmap': {
        ...(yAxis
          ? {
              yAxis: {
                type: 'category',
                data: yAxis,
                splitArea: { show: true },
              },
            }
          : {}),
        ...(xAxis
          ? {
              xAxis: {
                type: 'category',
                data: xAxis,
                splitArea: { show: true },
              },
            }
          : {}),
        visualMap: {
          min,
          max,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
        },
      },
      'apache-heatmap-calendar': {
        visualMap: {
          min,
          max,
          type: 'piecewise',
          orient: 'horizontal',
          left: 'center',
          top: 65,
          pieces: generatePieces({
            theme,
            ranges:
              ranges ??
              generateRangeArray({
                min,
                max,
                arrayLength: DEFAULT_HEATMAP_PIECES_COUNT,
              }),
          }),
        },
        calendar: {
          top: 120,
          left: 30,
          right: 30,
          cellSize: ['auto', 13],
          range: series.name,
          itemStyle: {
            borderWidth: 0.5,
          },
          yearLabel: { show: false },
        },
      },
    };
  }, [valueArr, ranges, theme, xAxis, yAxis]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        ...heatMapOptions[chartType],
        series: {
          name: series.name,
          data: formattedSeries,
          ...(chartType === 'apache-heatmap-calendar'
            ? { coordinateSystem: 'calendar' }
            : {}),
          type: 'heatmap',
        },
        legend: {
          show: false,
        },
        tooltip: {
          valueFormatter: value => {
            if (typeof value === 'number') {
              return formatDecimalPlaces(value).toString();
            }

            return value?.toString() ?? '';
          },
        },
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [
    chartType,
    theme,
    formattedSeries,
    heatMapOptions,
    series,
    width,
    height,
    isModal,
  ]);

  return <div ref={ref} />;
};
